import moment from 'moment-timezone'
import {useMemo} from 'react'

/**
 * Check if the current locale uses 12-hour format
 */
const has12HoursFormat = (): boolean => {
  const currentTime = moment().format('LT').toLocaleUpperCase()
  return currentTime.endsWith('AM') || currentTime.endsWith('PM')
}

/**
 * Format time based on locale's 12/24 hour preference
 */
const formatTime = (time: moment.Moment): string => {
  const is12HoursFormat = has12HoursFormat()
  return is12HoursFormat ? time.format('hh:mm A') : time.format('HH:mm')
}

/**
 * Result of timezone difference calculation
 */
export type TimezoneInformationResult = {
  /** Whether the device timezone differs from the job site timezone */
  isTimezoneDifferent: boolean
  /** Formatted current times for device and job site */
  currentTimes?: {
    deviceTime: string
    jobSiteTime: string
  }
  /** Timezone abbreviations for device and job site */
  timezones?: {
    deviceTimezone: string
    jobSiteTimezone: string
  }
  /** Time difference in hours between device and job site */
  timeDifference?: number
  /** Whether the device time is ahead of the job site time */
  isDeviceTimeAhead?: boolean
  /**
   * Day difference indicator.
   * Returns 1 if job site is a day ahead, -1 if a day behind, 0 if same day.
   */
  dayDifference?: number
}

/**
 * Parameters for timezone information hook
 */
export type UseTimezoneInformationParams = {
  /** The job site timezone (e.g., 'Australia/Sydney') */
  jobSiteTimezone?: string | null
  /** The selected date/time in ISO format */
  selectedDate?: string | null
}

/**
 * Calculate timezone difference between device and job site
 *
 * This is a pure utility function that can be used in both web and mobile apps.
 * It does not contain any strings - all text formatting should be done by the consumer.
 *
 * @param jobSiteTimezone - The IANA timezone of the job site (e.g., 'Australia/Sydney')
 * @param selectedDate - The selected date/time in ISO format
 * @returns TimezoneInformationResult with all timezone difference information
 */
export const getTimezoneInformation = (
  jobSiteTimezone?: string | null,
  selectedDate?: string | null
): TimezoneInformationResult => {
  if (!jobSiteTimezone) {
    return {isTimezoneDifferent: false}
  }

  const deviceTimezone = moment.tz.guess()

  const deviceTime = selectedDate
    ? moment(selectedDate).tz(deviceTimezone)
    : moment().tz(deviceTimezone)
  const jobSiteTime = selectedDate
    ? moment(selectedDate).tz(jobSiteTimezone)
    : moment().tz(jobSiteTimezone)

  const isTimezoneDifferent = deviceTime.utcOffset() !== jobSiteTime.utcOffset()
  const timeDifference = Math.abs(deviceTime.utcOffset() - jobSiteTime.utcOffset()) / 60
  const isDeviceTimeAhead = deviceTime.utcOffset() > jobSiteTime.utcOffset()

  // Calculate day difference: positive if job site is ahead, negative if behind
  const dayDifferenceCalc =
    jobSiteTime.clone().dayOfYear() - jobSiteTime.clone().tz(deviceTimezone).dayOfYear()

  return {
    isTimezoneDifferent,
    currentTimes: {
      deviceTime: formatTime(deviceTime),
      jobSiteTime: formatTime(jobSiteTime)
    },
    timezones: {
      deviceTimezone: deviceTime.format('z'),
      jobSiteTimezone: jobSiteTime.format('z')
    },
    timeDifference,
    isDeviceTimeAhead,
    dayDifference: dayDifferenceCalc
  }
}

/**
 * Normalize a date string to 12:00 PM UTC
 *
 * Useful for avoiding timezone edge cases when only the date matters.
 *
 * @param dateString - ISO date string
 * @returns Date string normalized to 12:00 PM UTC
 */
export const normalizeToTwelvePmUtc = (dateString: string): string => {
  return `${moment(dateString).format('YYYY-MM-DD')}T12:00:00.000Z`
}

/**
 * React hook to get timezone information based on job site and selected date
 *
 * This hook integrates with the form context to get the selected date/time
 * and accepts the job site timezone as a parameter.
 *
 * @param params - Parameters including jobSiteTimezone and optional selectedDate
 * @returns TimezoneInformationResult with memoized values
 *
 * @example
 * ```tsx
 * const {isTimezoneDifferent, currentTimes, timezones, dayDifference} = useTimezoneInformation({
 *   jobSiteTimezone: 'Australia/Sydney',
 *   selectedDate: '2024-01-15T08:00:00.000Z'
 * })
 *
 * if (isTimezoneDifferent) {
 *   // Show timezone banner with formatted message
 *   const message = t('timezone.jobSiteLocalTime', {
 *     time: currentTimes.jobSiteTime,
 *     timezone: timezones.jobSiteTimezone,
 *     dayDifference: dayDifference > 0 ? '+1 day' : dayDifference < 0 ? '-1 day' : ''
 *   })
 * }
 * ```
 */
export const useTimezoneInformation = ({
  jobSiteTimezone,
  selectedDate
}: UseTimezoneInformationParams): TimezoneInformationResult => {
  return useMemo(
    () => getTimezoneInformation(jobSiteTimezone, selectedDate),
    [jobSiteTimezone, selectedDate]
  )
}
